import {
  CODES,
  KEYS,
  CLASSES,
  POINTER_BUTTON,
  THEME,
  isWritableElement,
  getFontString,
  getFontFamilyString,
  isTestEnv,
  MIME_TYPES,
  applyDarkModeFilter,
  isRTL,
  hasRTLChars,
} from "@excalidraw/common";
import { pointFrom, pointRotateRads, type Radians } from "@excalidraw/math";

import {
  getTextFromElements,
  originalContainerCache,
  updateBoundElements,
  updateOriginalContainerCache,
} from "@excalidraw/element";

import { LinearElementEditor } from "@excalidraw/element";
import { bumpVersion } from "@excalidraw/element";
import {
  getBoundTextElementId,
  getContainerElement,
  getTextElementAngle,
  redrawTextBoundingBox,
  getBoundTextMaxHeight,
  getBoundTextMaxWidth,
  computeContainerDimensionForBoundText,
  computeBoundTextPosition,
  getBoundTextElement,
} from "@excalidraw/element";
import { getTextWidth } from "@excalidraw/element";
import { getLineHeightInPx } from "@excalidraw/element";
import { getLineWidth } from "@excalidraw/element";
import { normalizeText } from "@excalidraw/element";
import { wrapText } from "@excalidraw/element";
import { getWrappedTextLines } from "@excalidraw/element";
import {
  isArrowElement,
  isBoundToContainer,
  isStickyNoteElement,
  isTextElement,
} from "@excalidraw/element";

import type {
  ExcalidrawLinearElement,
  ExcalidrawTextElementWithContainer,
  ExcalidrawTextElement,
  NonDeleted,
  ExcalidrawTextContainer,
} from "@excalidraw/element/types";

import { actionSaveFileToDisk, actionSaveToActiveFile } from "../actions";

import {
  parseClipboard,
  parseDataTransferEvent,
  parseDataTransferEventMimeTypes,
} from "../clipboard";
import {
  actionDecreaseFontSize,
  actionIncreaseFontSize,
} from "../actions/actionProperties";
import {
  actionResetZoom,
  actionZoomIn,
  actionZoomOut,
} from "../actions/actionCanvas";

import type { ParsedDataTranferList } from "../clipboard";

import type App from "../components/App";
import type { AppState } from "../types";

/**
 * How much further than the browser's caret reveal the canvas pans, in
 * screen px: text typed past the viewport's edge comes back with this much
 * room to spare, instead of flush against the edge.
 */
export const CARET_FOLLOW_PADDING = 5;

/**
 * The editor is scaled and rotated about the text's center (its transform
 * origin), as the canvas draws the text. The zoom puts that center at half
 * the *scaled* size from the box's top-left, while the origin sits at half
 * the unscaled size — the translate makes up the difference.
 */
const getTransform = (
  width: number,
  height: number,
  angle: number,
  appState: AppState,
) => {
  const { zoom } = appState;
  const degree = (180 * angle) / Math.PI;
  const translateX = (width * (zoom.value - 1)) / 2;
  const translateY = (height * (zoom.value - 1)) / 2;
  return `translate(${translateX}px, ${translateY}px) scale(${zoom.value}) rotate(${degree}deg)`;
};

const getLineDirection = (text: string, offset: number) => {
  const hardLineStart = text.lastIndexOf("\n", Math.max(0, offset - 1)) + 1;
  const hardLineEnd = text.indexOf("\n", offset);
  const hardLineText = text.slice(
    hardLineStart,
    hardLineEnd === -1 ? text.length : hardLineEnd,
  );

  return isRTL(hardLineText) ? "rtl" : "ltr";
};

const getCaretBoundaryOffsets = (text: string) => {
  const offsets = [0];
  let offset = 0;

  for (const char of Array.from(text)) {
    offset += char.length;
    offsets.push(offset);
  }

  return offsets;
};

type NativeLineLayout = {
  text: string;
  font: ReturnType<typeof getFontString>;
  lineHeightPx: number;
  direction: "ltr" | "rtl";
  ownerDocument: Document;
};

/**
 * Where the caret shows at each of `offsets` into a line of text, as the
 * browser lays the line out, bidirectionally: the x of each (px, from the
 * viewport's left), and of the line's left edge. Null where it can't tell.
 */
const measureNativeLineCaretPositions = ({
  text,
  font,
  lineHeightPx,
  direction,
  ownerDocument,
  offsets,
}: NativeLineLayout & { offsets: readonly number[] }) => {
  if (
    !text ||
    !ownerDocument.body ||
    typeof ownerDocument.createRange !== "function"
  ) {
    return null;
  }

  const mirror = ownerDocument.createElement("div");
  const textNode = ownerDocument.createTextNode(text);
  const range = ownerDocument.createRange();
  const positions: number[] = [];

  mirror.dir = direction;
  Object.assign(mirror.style, {
    position: "fixed",
    top: "0",
    left: "0",
    margin: 0,
    padding: 0,
    border: 0,
    opacity: "0",
    pointerEvents: "none",
    whiteSpace: "pre",
    font,
    lineHeight: `${lineHeightPx}px`,
  });
  mirror.append(textNode);
  ownerDocument.body.append(mirror);

  try {
    range.selectNodeContents(textNode);
    const { left } = range.getBoundingClientRect();
    for (const offset of offsets) {
      range.setStart(textNode, offset);
      range.setEnd(textNode, offset);
      const caretRect = range.getBoundingClientRect();

      if (!Number.isFinite(caretRect.left)) {
        return null;
      }

      positions.push(caretRect.left);
    }
    return { left, positions };
  } catch {
    return null;
  } finally {
    mirror.remove();
  }
};

const getLineCaretOffsetFromNativeLayout = ({
  targetX,
  ...layout
}: NativeLineLayout & { targetX: number }) => {
  const offsets = getCaretBoundaryOffsets(layout.text);
  const positions = measureNativeLineCaretPositions({
    ...layout,
    offsets,
  })?.positions;
  if (!positions) {
    return null;
  }

  const leftEdge = Math.min(...positions);
  let closestOffset = offsets[0];
  let closestDistance = Infinity;

  for (let index = 0; index < offsets.length; index++) {
    const distance = Math.abs(positions[index] - leftEdge - targetX);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestOffset = offsets[index];
    }
  }

  return closestOffset;
};

/**
 * Where the caret shows at `offset` into a line of text, as the browser lays
 * the line out, bidirectionally: px from the line's left edge; null where it
 * can't tell.
 */
const getLineCaretXFromNativeLayout = ({
  offset,
  ...layout
}: NativeLineLayout & { offset: number }) => {
  const measured = measureNativeLineCaretPositions({
    ...layout,
    offsets: [offset],
  });
  return measured ? measured.positions[0] - measured.left : null;
};

type SubmitHandler = () => void;

export const textWysiwyg = ({
  onChange,
  onSubmit,
  getViewportCoords,
  element,
  canvas,
  excalidrawContainer,
  app,
  autoSelect = true,
  initialCaretSceneCoords = null,
  initialSelection = null,
}: {
  /**
   * textWysiwyg only deals with `originalText`
   *
   * Note: `text`, which can be wrapped and therefore different from `originalText`,
   *       is derived from `originalText`
   */
  onChange?: (nextOriginalText: string) => void;
  onSubmit: (data: { viaKeyboard: boolean; nextOriginalText: string }) => void;
  getViewportCoords: (x: number, y: number) => [number, number];
  element: ExcalidrawTextElement;
  canvas: HTMLCanvasElement;
  excalidrawContainer: HTMLDivElement | null;
  app: App;
  autoSelect?: boolean;
  initialCaretSceneCoords?: { x: number; y: number } | null;
  /** range of `originalText` to select (takes precedence over the caret) */
  initialSelection?: { start: number; end: number } | null;
}): SubmitHandler => {
  const ownerDocument = excalidrawContainer?.ownerDocument ?? document;
  const ownerWindow = ownerDocument.defaultView ?? window;
  // the editor's box: the part of the canvas a caret is revealed into (see
  // onEditorBoxScroll)
  const editorBox =
    excalidrawContainer?.querySelector<HTMLDivElement>(
      ".excalidraw-textEditorContainer",
    ) ?? null;

  /**
   * Keeps the editor's box off the sidebar, so a caret behind the sidebar
   * is outside the box and gets revealed like one past the viewport's edge.
   * Returns the box's left inset, which the editor's position is relative
   * to.
   */
  const updateEditorBoxInsets = () => {
    if (!editorBox) {
      return 0;
    }
    const { left, right } = app.viewport.getSidebarInsets();
    editorBox.style.left = `${left}px`;
    editorBox.style.right = `${right}px`;
    return left;
  };
  let currentTextLayout: {
    angle: Radians;
    font: ReturnType<typeof getFontString>;
    height: number;
    lineHeightPx: number;
    textAlign: ExcalidrawTextElement["textAlign"];
    width: number;
    x: number;
    y: number;
  } | null = null;

  const textPropertiesUpdated = (
    updatedTextElement: ExcalidrawTextElement,
    editable: HTMLTextAreaElement,
  ) => {
    if (!editable.style.fontFamily || !editable.style.fontSize) {
      return false;
    }
    const currentFont = editable.style.fontFamily.replace(/"/g, "");
    if (
      getFontFamilyString({ fontFamily: updatedTextElement.fontFamily }) !==
      currentFont
    ) {
      return true;
    }
    if (`${updatedTextElement.fontSize}px` !== editable.style.fontSize) {
      return true;
    }
    return false;
  };

  let LAST_THEME = app.state.theme;

  const updateWysiwygStyle = () => {
    LAST_THEME = app.state.theme;

    const appState = app.state;
    const updatedTextElement = app.scene.getElement<
      NonDeleted<ExcalidrawTextElement>
    >(element.id);

    if (!updatedTextElement) {
      return;
    }
    const { textAlign, verticalAlign } = updatedTextElement;
    const elementsMap = app.scene.getNonDeletedElementsMap();
    if (updatedTextElement && isTextElement(updatedTextElement)) {
      let coordX = updatedTextElement.x;
      let coordY = updatedTextElement.y;
      const container = getContainerElement<
        NonDeleted<ExcalidrawTextElement>,
        NonDeleted<ExcalidrawTextContainer>
      >(updatedTextElement, app.scene.getNonDeletedElementsMap());

      let width = updatedTextElement.width;

      // set to element height by default since that's
      // what is going to be used for unbounded text
      let height = updatedTextElement.height;

      let maxHeight = updatedTextElement.height;

      if (container && updatedTextElement.containerId) {
        if (isArrowElement(container)) {
          const boundTextCoords =
            LinearElementEditor.getBoundTextElementPosition(
              container,
              updatedTextElement as ExcalidrawTextElementWithContainer,
              elementsMap,
            );
          coordX = boundTextCoords.x;
          coordY = boundTextCoords.y;
        }
        maxHeight = getBoundTextMaxHeight(
          container,
          updatedTextElement as ExcalidrawTextElementWithContainer,
        );

        if (isStickyNoteElement(container)) {
          // the sticky fit (App.updateElement) owns the note's height; the
          // editor only mirrors the fitted label's position
          const { x, y } = computeBoundTextPosition(
            container,
            updatedTextElement as ExcalidrawTextElementWithContainer,
            elementsMap,
          );
          coordX = x;
          coordY = y;
        } else {
          const propertiesUpdated = textPropertiesUpdated(
            updatedTextElement,
            editable,
          );

          let originalContainerData;
          if (propertiesUpdated) {
            originalContainerData = updateOriginalContainerCache(
              container.id,
              container.height,
            );
          } else {
            originalContainerData = originalContainerCache[container.id];
            if (!originalContainerData) {
              originalContainerData = updateOriginalContainerCache(
                container.id,
                container.height,
              );
            }
          }

          // autogrow container height if text exceeds
          if (!isArrowElement(container) && height > maxHeight) {
            const targetContainerHeight = computeContainerDimensionForBoundText(
              height,
              container.type,
            );

            app.scene.mutateElement(container, {
              height: targetContainerHeight,
            });
            updateBoundElements(container, app.scene);
            return;
          } else if (
            // autoshrink container height until original container height
            // is reached when text is removed
            !isArrowElement(container) &&
            container.height > originalContainerData.height &&
            height < maxHeight
          ) {
            const targetContainerHeight = computeContainerDimensionForBoundText(
              height,
              container.type,
            );
            app.scene.mutateElement(container, {
              height: targetContainerHeight,
            });
            updateBoundElements(container, app.scene);
          } else {
            const { x, y } = computeBoundTextPosition(
              container,
              updatedTextElement as ExcalidrawTextElementWithContainer,
              elementsMap,
            );
            coordX = x;
            coordY = y;
          }
        }
      }
      const [viewportX, viewportY] = getViewportCoords(coordX, coordY);
      const angle = getTextElementAngle(updatedTextElement, container);

      // The editor box is the text's, never cut to the viewport: a caret past
      // the viewport's edge is revealed by panning the canvas (see
      // onEditorBoxScroll), and the editor stays on its text.
      if (container) {
        width += 0.5;
      }

      // add 5% buffer otherwise it causes wysiwyg to jump
      height *= 1.05;

      const font = getFontString(updatedTextElement);
      const editorBoxLeft = updateEditorBoxInsets();

      // a free text that stopped growing at the viewport's width (see
      // AppText.getMaxTextWidth) wraps from then on, and so does its editor
      if (
        !updatedTextElement.autoResize &&
        editable.style.whiteSpace !== "pre-wrap"
      ) {
        editable.style.whiteSpace = "pre-wrap";
        editable.style.wordBreak = "break-word";
      }

      Object.assign(editable.style, {
        font,
        // must be defined *after* font ¯\_(ツ)_/¯
        lineHeight: updatedTextElement.lineHeight,
        width: `${width}px`,
        height: `${height}px`,
        left: `${viewportX - editorBoxLeft}px`,
        top: `${viewportY}px`,
        // about the text's center, whatever size the box itself ends up
        // (the 5% buffer) — see getTransform
        transformOrigin: `${updatedTextElement.width / 2}px ${
          updatedTextElement.height / 2
        }px`,
        transform: getTransform(
          updatedTextElement.width,
          updatedTextElement.height,
          angle,
          appState,
        ),
        textAlign,
        verticalAlign,
        color: applyDarkModeFilter(
          updatedTextElement.strokeColor,
          appState.theme === THEME.DARK,
        ),
        opacity: updatedTextElement.opacity / 100,
      });
      currentTextLayout = {
        angle: angle as Radians,
        font,
        height: updatedTextElement.height,
        lineHeightPx: getLineHeightInPx(
          updatedTextElement.fontSize,
          updatedTextElement.lineHeight,
        ),
        textAlign,
        width: updatedTextElement.width,
        x: coordX,
        y: coordY,
      };
      editable.scrollTop = 0;
      // For some reason updating font attribute doesn't set font family
      // hence updating font family explicitly for test environment
      if (isTestEnv()) {
        editable.style.fontFamily = getFontFamilyString(updatedTextElement);
      }

      app.scene.mutateElement(updatedTextElement, { x: coordX, y: coordY });
    }
  };

  const editable = ownerDocument.createElement("textarea");

  editable.dir = "auto";
  editable.tabIndex = 0;
  editable.dataset.type = "wysiwyg";
  // prevent line wrapping on Safari
  editable.wrap = "off";
  editable.classList.add("excalidraw-wysiwyg");

  let whiteSpace = "pre";
  let wordBreak = "normal";

  if (isBoundToContainer(element) || !element.autoResize) {
    whiteSpace = "pre-wrap";
    wordBreak = "break-word";
  }
  Object.assign(editable.style, {
    position: "absolute",
    display: "inline-block",
    minHeight: "1em",
    backfaceVisibility: "hidden",
    margin: 0,
    padding: 0,
    border: 0,
    outline: 0,
    resize: "none",
    background: "transparent",
    overflow: "hidden",
    // must be specified because in dark mode canvas creates a stacking context
    zIndex: "var(--zIndex-wysiwyg)",
    wordBreak,
    // prevent line wrapping (`whitespace: nowrap` doesn't work on FF)
    whiteSpace,
    overflowWrap: "break-word",
    boxSizing: "content-box",
  });
  editable.value = element.originalText;
  updateWysiwygStyle();

  const getCaretIndexFromInitialSceneCoords = () => {
    if (!initialCaretSceneCoords || !currentTextLayout) {
      return null;
    }

    const layout = currentTextLayout;
    const center = pointFrom(
      layout.x + layout.width / 2,
      layout.y + layout.height / 2,
    );
    const [unrotatedX, unrotatedY] = pointRotateRads(
      pointFrom(initialCaretSceneCoords.x, initialCaretSceneCoords.y),
      center,
      -layout.angle as Radians,
    );
    const localX = unrotatedX - layout.x;
    const localY = unrotatedY - layout.y;
    const lines = getWrappedTextLines(
      editable.value,
      layout.font,
      whiteSpace === "pre-wrap" ? layout.width : Infinity,
    );
    const lineIndex = Math.max(
      0,
      Math.min(lines.length - 1, Math.floor(localY / layout.lineHeightPx)),
    );
    const line = lines[lineIndex];
    const direction = getLineDirection(editable.value, line.start);
    const lineWidth = getLineWidth(line.text, layout.font);
    const lineStartX =
      layout.textAlign === "center"
        ? (layout.width - lineWidth) / 2
        : layout.textAlign === "right"
        ? layout.width - lineWidth
        : 0;
    const relativeX = localX - lineStartX;

    if (!line.text) {
      return line.start;
    }

    const lineCaretOffset = getLineCaretOffsetFromNativeLayout({
      text: line.text,
      font: layout.font,
      lineHeightPx: layout.lineHeightPx,
      direction,
      targetX: relativeX,
      ownerDocument,
    });

    return line.start + (lineCaretOffset || 0);
  };

  /**
   * Where the caret is on screen (see getViewportCoords), as the editor lays
   * the text out: the bounds of its line-high stroke.
   */
  const getCaretViewportBounds = () => {
    const layout = currentTextLayout;
    if (!layout) {
      return null;
    }
    const { value } = editable;
    const caret =
      editable.selectionDirection === "backward"
        ? editable.selectionStart
        : editable.selectionEnd;
    const lines = getWrappedTextLines(
      value,
      layout.font,
      editable.style.whiteSpace === "pre-wrap" ? layout.width : Infinity,
    );
    // the last line starting at or before it: at a soft break, the caret
    // shows at the start of the next line
    const lineIndex = Math.max(
      0,
      lines.findLastIndex((line) => line.start <= caret),
    );
    const line = lines[lineIndex];
    const lineWidth = getLineWidth(line.text, layout.font);
    const lineStartX =
      layout.textAlign === "center"
        ? (layout.width - lineWidth) / 2
        : layout.textAlign === "right"
        ? layout.width - lineWidth
        : 0;
    const lineText = value.slice(line.start, line.end);
    const offset = Math.min(caret, line.end) - line.start;
    const widthBefore = getLineWidth(lineText.slice(0, offset), layout.font);
    // the editor's `dir="auto"` makes it `unicode-bidi: plaintext`: each
    // paragraph goes its first strong character's way
    const direction = getLineDirection(value, line.start);
    // any RTL character in it, and the browser's bidirectional layout of
    // the line says where the caret is
    const nativeX = hasRTLChars(lineText)
      ? getLineCaretXFromNativeLayout({
          text: lineText,
          font: layout.font,
          lineHeightPx: layout.lineHeightPx,
          direction,
          ownerDocument,
          offset,
        })
      : null;
    const caretX =
      lineStartX +
      (nativeX ??
        (direction === "rtl" ? lineWidth - widthBefore : widthBefore));
    const center = pointFrom(
      layout.x + layout.width / 2,
      layout.y + layout.height / 2,
    );
    const [[x1, y1], [x2, y2]] = [lineIndex, lineIndex + 1].map((index) => {
      const [x, y] = pointRotateRads(
        pointFrom(layout.x + caretX, layout.y + index * layout.lineHeightPx),
        center,
        layout.angle,
      );
      return getViewportCoords(x, y);
    });
    return {
      left: Math.min(x1, x2),
      right: Math.max(x1, x2),
      top: Math.min(y1, y2),
      bottom: Math.max(y1, y2),
    };
  };

  /**
   * A caret typed in under the stats or the styles panel (see
   * AppText.getTextSidePanels) is out of sight, yet inside the editor's box, so
   * the browser doesn't reveal it (unlike the sidebar, the panels don't span
   * the box's height, see updateEditorBoxInsets). Pan the canvas to bring it
   * out from under the panel, on the canvas' side, with some room to spare —
   * only then: a text beside or below a panel stays where it is.
   */
  const followCaretFromUnderPanels = () => {
    const caret = !isDestroyed && getCaretViewportBounds();
    if (!caret) {
      return;
    }
    for (const name of app.text.getTextSidePanels()) {
      const panel = app.viewport.getSideUIRect(name);
      if (
        panel &&
        caret.right >= panel.left &&
        caret.left <= panel.right &&
        caret.bottom > panel.top &&
        caret.top < panel.bottom
      ) {
        const panX =
          panel.left + panel.right < app.state.width
            ? panel.right + CARET_FOLLOW_PADDING - caret.left
            : panel.left - CARET_FOLLOW_PADDING - caret.right;
        app.viewport.translate((state) => ({
          scrollX: state.scrollX + panX / state.zoom.value,
        }));
        return;
      }
    }
  };

  let pendingInitialSelection =
    initialSelection ??
    (() => {
      const caretIndex = getCaretIndexFromInitialSceneCoords();

      if (caretIndex === null) {
        return null;
      }

      return {
        start: caretIndex,
        end: caretIndex,
      };
    })();

  if (onChange) {
    editable.onpaste = async (event) => {
      // we need to synchronously get the MIME types so we can preventDefault()
      // in the same tick (FF requires that)
      const mimeTypes = parseDataTransferEventMimeTypes(event);

      let dataList: ParsedDataTranferList | null = null;

      // when copy/pasting excalidraw elements, only paste the text content
      //
      // Note that these custom MIME types only work within the same family
      // of browsers, so won't work e.g. between chrome and firefox. We could
      // parse the text/plain for existence of excalidraw instead, but this
      // is an edge case
      if (
        mimeTypes.has(MIME_TYPES.excalidrawClipboard) ||
        mimeTypes.has(MIME_TYPES.excalidraw)
      ) {
        // must be called in the same tick
        event.preventDefault();

        dataList = await parseDataTransferEvent(event);

        try {
          const parsed = await parseClipboard(dataList);

          if (parsed.elements) {
            const text = getTextFromElements(parsed.elements);
            if (text) {
              const { selectionStart, selectionEnd, value } = editable;

              editable.value =
                value.slice(0, selectionStart) +
                text +
                value.slice(selectionEnd);

              const newPos = selectionStart + text.length;
              editable.selectionStart = editable.selectionEnd = newPos;

              editable.dispatchEvent(new Event("input"));
            }
          }

          // if excalidraw elements don't contain any text elements,
          // don't paste anything
          return;
        } catch {
          console.warn("failed to parse excalidraw clipboard data");
        }
      }

      dataList = dataList || (await parseDataTransferEvent(event));

      const textItem = dataList.findByType(MIME_TYPES.text);
      if (!textItem) {
        return;
      }
      const text = normalizeText(textItem.value);
      if (!text) {
        return;
      }
      const container = getContainerElement(
        element,
        app.scene.getNonDeletedElementsMap(),
      );

      if (container) {
        const boundTextElement = getBoundTextElement(
          container,
          app.scene.getNonDeletedElementsMap(),
        );
        const font = getFontString({
          fontSize:
            isStickyNoteElement(container) && boundTextElement
              ? boundTextElement.fontSize
              : app.state.currentItemFontSize,
          fontFamily:
            isStickyNoteElement(container) && boundTextElement
              ? boundTextElement.fontFamily
              : app.state.currentItemFontFamily,
        });
        const maxWidth = getBoundTextMaxWidth(container, boundTextElement);
        const { selectionStart, selectionEnd, value } = editable;
        const nextText =
          value.slice(0, selectionStart) + text + value.slice(selectionEnd);
        const wrappedText = wrapText(nextText, font, maxWidth);
        const width = Math.min(getTextWidth(wrappedText, font), maxWidth);
        editable.style.width = `${width}px`;
      }
    };

    editable.oninput = () => {
      const normalized = normalizeText(editable.value);
      if (editable.value !== normalized) {
        const selectionStart = editable.selectionStart;
        editable.value = normalized;
        // put the cursor at some position close to where it was before
        // normalization (otherwise it'll end up at the end of the text)
        editable.selectionStart = selectionStart;
        editable.selectionEnd = selectionStart;
      }
      onChange(editable.value);
      // once the update, and any scroll it made (see
      // AppText.handleTextWysiwyg), is in the app's state
      queueMicrotask(followCaretFromUnderPanels);
    };
  }

  editable.onkeydown = (event) => {
    if (!event.shiftKey && actionZoomIn.keyTest(event)) {
      event.preventDefault();
      app.actionManager.executeAction(actionZoomIn);
      updateWysiwygStyle();
    } else if (!event.shiftKey && actionZoomOut.keyTest(event)) {
      event.preventDefault();
      app.actionManager.executeAction(actionZoomOut);
      updateWysiwygStyle();
    } else if (!event.shiftKey && actionResetZoom.keyTest(event)) {
      event.preventDefault();
      app.actionManager.executeAction(actionResetZoom);
      updateWysiwygStyle();
    } else if (actionDecreaseFontSize.keyTest(event)) {
      app.actionManager.executeAction(actionDecreaseFontSize);
    } else if (actionIncreaseFontSize.keyTest(event)) {
      app.actionManager.executeAction(actionIncreaseFontSize);
    } else if (event.key === KEYS.ESCAPE) {
      event.preventDefault();
      submittedViaKeyboard = true;
      handleSubmit();
    } else if (actionSaveToActiveFile.keyTest(event)) {
      event.preventDefault();
      handleSubmit();
      app.actionManager.executeAction(actionSaveToActiveFile, "keyboard");
    } else if (actionSaveFileToDisk.keyTest(event)) {
      event.preventDefault();
      handleSubmit();
      app.actionManager.executeAction(actionSaveFileToDisk, "keyboard");
    } else if (event.key === KEYS.ENTER && event[KEYS.CTRL_OR_CMD]) {
      event.preventDefault();
      if (event.isComposing || event.keyCode === 229) {
        return;
      }
      submittedViaKeyboard = true;
      handleSubmit();
    } else if (
      event.key === KEYS.TAB ||
      (event[KEYS.CTRL_OR_CMD] &&
        (event.code === CODES.BRACKET_LEFT ||
          event.code === CODES.BRACKET_RIGHT))
    ) {
      event.preventDefault();
      if (event.isComposing) {
        return;
      } else if (event.shiftKey || event.code === CODES.BRACKET_LEFT) {
        outdent();
      } else {
        indent();
      }
      // We must send an input event to resize the element
      editable.dispatchEvent(new Event("input"));
    }
  };

  const TAB_SIZE = 4;
  const TAB = " ".repeat(TAB_SIZE);
  const RE_LEADING_TAB = new RegExp(`^ {1,${TAB_SIZE}}`);
  const indent = () => {
    const { selectionStart, selectionEnd } = editable;
    const linesStartIndices = getSelectedLinesStartIndices();

    let value = editable.value;
    linesStartIndices.forEach((startIndex: number) => {
      const startValue = value.slice(0, startIndex);
      const endValue = value.slice(startIndex);

      value = `${startValue}${TAB}${endValue}`;
    });

    editable.value = value;

    editable.selectionStart = selectionStart + TAB_SIZE;
    editable.selectionEnd = selectionEnd + TAB_SIZE * linesStartIndices.length;
  };

  const outdent = () => {
    const { selectionStart, selectionEnd } = editable;
    const linesStartIndices = getSelectedLinesStartIndices();
    const removedTabs: number[] = [];

    let value = editable.value;
    linesStartIndices.forEach((startIndex) => {
      const tabMatch = value
        .slice(startIndex, startIndex + TAB_SIZE)
        .match(RE_LEADING_TAB);

      if (tabMatch) {
        const startValue = value.slice(0, startIndex);
        const endValue = value.slice(startIndex + tabMatch[0].length);

        // Delete a tab from the line
        value = `${startValue}${endValue}`;
        removedTabs.push(startIndex);
      }
    });

    editable.value = value;

    if (removedTabs.length) {
      if (selectionStart > removedTabs[removedTabs.length - 1]) {
        editable.selectionStart = Math.max(
          selectionStart - TAB_SIZE,
          removedTabs[removedTabs.length - 1],
        );
      } else {
        // If the cursor is before the first tab removed, ex:
        // Line| #1
        //     Line #2
        // Lin|e #3
        // we should reset the selectionStart to his initial value.
        editable.selectionStart = selectionStart;
      }
      editable.selectionEnd = Math.max(
        editable.selectionStart,
        selectionEnd - TAB_SIZE * removedTabs.length,
      );
    }
  };

  /**
   * @returns indices of start positions of selected lines, in reverse order
   */
  const getSelectedLinesStartIndices = () => {
    let { selectionStart, selectionEnd, value } = editable;

    // chars before selectionStart on the same line
    const startOffset = value.slice(0, selectionStart).match(/[^\n]*$/)![0]
      .length;
    // put caret at the start of the line
    selectionStart = selectionStart - startOffset;

    const selected = value.slice(selectionStart, selectionEnd);

    return selected
      .split("\n")
      .reduce(
        (startIndices, line, idx, lines) =>
          startIndices.concat(
            idx
              ? // curr line index is prev line's start + prev line's length + \n
                startIndices[idx - 1] + lines[idx - 1].length + 1
              : // first selected line
                selectionStart,
          ),
        [] as number[],
      )
      .reverse();
  };

  const stopEvent = (event: Event) => {
    if (event.target instanceof ownerWindow.HTMLCanvasElement) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // using a state variable instead of passing it to the handleSubmit callback
  // so that we don't need to create separate a callback for event handlers
  let submittedViaKeyboard = false;
  const handleSubmit = () => {
    // prevent double submit
    if (isDestroyed) {
      return;
    }

    isDestroyed = true;
    // cleanup must be run before onSubmit otherwise when app blurs the wysiwyg
    // it'd get stuck in an infinite loop of blur→onSubmit after we re-focus the
    // wysiwyg on update
    cleanup();
    const updateElement = app.scene.getElement(
      element.id,
    ) as ExcalidrawTextElement;
    if (!updateElement) {
      return;
    }
    const container = getContainerElement(
      updateElement,
      app.scene.getNonDeletedElementsMap(),
    );

    if (container) {
      if (editable.value.trim()) {
        const boundTextElementId = getBoundTextElementId(container);
        if (!boundTextElementId || boundTextElementId !== element.id) {
          app.scene.mutateElement(container, {
            boundElements: (container.boundElements || []).concat({
              type: "text",
              id: element.id,
            }),
          });
        } else if (isArrowElement(container)) {
          // updating an arrow label may change bounds, prevent stale cache:
          bumpVersion(container);
        }
      } else {
        app.scene.mutateElement(container, {
          boundElements: container.boundElements?.filter(
            (ele) =>
              !isTextElement(
                ele as ExcalidrawTextElement | ExcalidrawLinearElement,
              ),
          ),
        });
      }

      redrawTextBoundingBox(updateElement, container, app.scene);
    }

    onSubmit({
      viaKeyboard: submittedViaKeyboard,
      nextOriginalText: editable.value,
    });
  };

  const cleanup = () => {
    // remove events to ensure they don't late-fire
    editable.onblur = null;
    editable.oninput = null;
    editable.onkeydown = null;

    if (observer) {
      observer.disconnect();
    }

    ownerWindow.removeEventListener("resize", updateWysiwygStyle);
    ownerWindow.removeEventListener("wheel", stopEvent, true);
    ownerWindow.removeEventListener("pointerdown", onPointerDown);
    ownerWindow.removeEventListener("pointerup", bindBlurEvent);
    ownerWindow.removeEventListener("blur", handleSubmit);
    ownerWindow.removeEventListener("beforeunload", handleSubmit);
    unbindUpdate();
    unsubOnChange();
    unbindOnScroll();
    editorBox?.removeEventListener("scroll", onEditorBoxScroll);
    editorBox?.style.removeProperty("left");
    editorBox?.style.removeProperty("right");

    editable.remove();
  };

  const bindBlurEvent = (event?: MouseEvent) => {
    ownerWindow.removeEventListener("pointerup", bindBlurEvent);
    // Deferred so that the pointerdown that initiates the wysiwyg doesn't
    // trigger the blur on ensuing pointerup.
    // Also to handle cases such as picking a color which would trigger a blur
    // in that same tick.
    const target = event?.target;

    const isPropertiesTrigger =
      target instanceof ownerWindow.HTMLElement &&
      target.classList.contains("properties-trigger");
    const isPropertiesContent =
      (target instanceof ownerWindow.HTMLElement ||
        target instanceof ownerWindow.SVGElement) &&
      !!(target as Element).closest(".properties-content");
    const inShapeActionsMenu =
      (target instanceof ownerWindow.HTMLElement ||
        target instanceof ownerWindow.SVGElement) &&
      (!!(target as Element).closest(`.${CLASSES.SHAPE_ACTIONS_MENU}`) ||
        !!(target as Element).closest(".compact-shape-actions-island"));

    ownerWindow.setTimeout(() => {
      // If we interacted within shape actions menu or its popovers/triggers,
      // keep submit disabled and don't steal focus back to textarea.
      if (inShapeActionsMenu || isPropertiesTrigger || isPropertiesContent) {
        return;
      }

      // Otherwise, re-enable submit on blur and refocus the editor. Never
      // let the focus scroll the container: for a box reaching past the
      // viewport, revealing the caret the textarea has until the click's is
      // placed (the end of the value) would pan the canvas there.
      editable.onblur = handleSubmit;
      editable.focus({ preventScroll: true });
      if (pendingInitialSelection) {
        editable.setSelectionRange(
          pendingInitialSelection.start,
          pendingInitialSelection.end,
        );
        pendingInitialSelection = null;
      }
    });
  };

  const temporarilyDisableSubmit = () => {
    editable.onblur = null;
    ownerWindow.addEventListener("pointerup", bindBlurEvent);
    // handle edge-case where pointerup doesn't fire e.g. due to user
    // alt-tabbing away
    ownerWindow.addEventListener("blur", handleSubmit);
  };

  // prevent blur when changing properties from the menu
  const onPointerDown = (event: PointerEvent) => {
    const target = event?.target;

    // panning canvas
    if (event.button === POINTER_BUTTON.WHEEL) {
      // trying to pan by clicking inside text area itself -> handle here
      if (target instanceof ownerWindow.HTMLTextAreaElement) {
        event.preventDefault();
        app.pan.start(event);
      }

      temporarilyDisableSubmit();
      return;
    }

    // alt+drag on the text being edited duplicates it, as on the canvas:
    // finish the edit keeping the text selected, then hand the press to the
    // canvas (the editor is gone by then, so the canvas receives the drag)
    if (
      event.altKey &&
      !event[KEYS.CTRL_OR_CMD] &&
      event.button === POINTER_BUTTON.MAIN &&
      target === editable &&
      (app.state.activeTool.type === "selection" ||
        app.state.activeTool.type === "lasso")
    ) {
      event.preventDefault();
      event.stopPropagation();
      submittedViaKeyboard = true;
      handleSubmit();
      app.duplicate.handOverTextEditorPress(event, element.id);
      return;
    }

    const isPropertiesTrigger =
      target instanceof ownerWindow.HTMLElement &&
      target.classList.contains("properties-trigger");
    const isPropertiesContent =
      (target instanceof ownerWindow.HTMLElement ||
        target instanceof ownerWindow.SVGElement) &&
      !!(target as Element).closest(".properties-content");

    if (
      ((event.target instanceof ownerWindow.HTMLElement ||
        event.target instanceof ownerWindow.SVGElement) &&
        (event.target.closest(
          `.${CLASSES.SHAPE_ACTIONS_MENU}, .${CLASSES.ZOOM_ACTIONS}`,
        ) ||
          event.target.closest(".compact-shape-actions-island")) &&
        !isWritableElement(event.target)) ||
      isPropertiesTrigger ||
      isPropertiesContent
    ) {
      temporarilyDisableSubmit();
    } else if (
      event.target instanceof ownerWindow.HTMLCanvasElement &&
      // Vitest simply ignores stopPropagation, capture-mode, or rAF
      // so without introducing crazier hacks, nothing we can do
      !isTestEnv()
    ) {
      // On mobile, blur event doesn't seem to always fire correctly,
      // so we want to also submit on pointerdown outside the wysiwyg.
      // Done in the next frame to prevent pointerdown from creating a new text
      // immediately (if tools locked) so that users on mobile have chance
      // to submit first (to hide virtual keyboard).
      // Note: revisit if we want to differ this behavior on Desktop
      ownerWindow.requestAnimationFrame(() => {
        handleSubmit();
      });
    }
  };

  // FIXME after we start emitting updates from Store for appState.theme
  const unsubOnChange = app.onChangeEmitter.on((elements) => {
    if (app.state.theme !== LAST_THEME) {
      updateWysiwygStyle();
    }
  });

  // handle updates of textElement properties of editing element
  const unbindUpdate = app.scene.onUpdate(() => {
    updateWysiwygStyle();
    const isPopupOpened = !!ownerDocument.activeElement?.closest(
      ".properties-content",
    );
    if (!isPopupOpened) {
      editable.focus({ preventScroll: true });
    }
  });

  const unbindOnScroll = app.onScrollChangeEmitter.on(() => {
    updateWysiwygStyle();
  });

  // The browser reveals an out-of-view caret by scrolling the nearest scroll
  // container: the editor's box, which clips it to the canvas area (off a
  // sidebar too, see updateEditorBoxInsets). Scrolled, the editor
  // would leave its text on the canvas; hand the offset to the canvas
  // instead, plus some room to spare, and put the box back: the canvas
  // follows the caret. Scroll events fire before the frame is painted, so
  // the box's shift is never seen, and the reveal never gets past the box —
  // to the editor root, whose overflow a host may override, or to a page
  // around an embedded editor. (A `scroll-padding` can't give the room: the
  // reveal only scrolls as far as the editor reaches, which ends at the
  // text.)
  const onEditorBoxScroll = () => {
    if (!editorBox) {
      return;
    }
    const { scrollLeft, scrollTop } = editorBox;
    if (!scrollLeft && !scrollTop) {
      return;
    }
    editorBox.scrollLeft = 0;
    editorBox.scrollTop = 0;
    // the box only scrolls right and down (it can't go below 0)
    const panX = scrollLeft && scrollLeft + CARET_FOLLOW_PADDING;
    const panY = scrollTop && scrollTop + CARET_FOLLOW_PADDING;
    app.viewport.translate((state) => ({
      scrollX: state.scrollX - panX / state.zoom.value,
      scrollY: state.scrollY - panY / state.zoom.value,
    }));
  };
  editorBox?.addEventListener("scroll", onEditorBoxScroll);

  // ---------------------------------------------------------------------------

  let isDestroyed = false;

  if (autoSelect && !pendingInitialSelection) {
    // select on init (focusing is done separately inside the bindBlurEvent()
    // because we need it to happen *after* the blur event from `pointerdown`)
    editable.select();
  }
  bindBlurEvent();

  // reposition wysiwyg in case of canvas is resized. Using ResizeObserver
  // is preferred so we catch changes from host, where window may not resize.
  let observer: ResizeObserver | null = null;
  if (canvas && "ResizeObserver" in ownerWindow) {
    observer = new ownerWindow.ResizeObserver(() => {
      updateWysiwygStyle();
    });
    observer.observe(canvas);
  } else {
    ownerWindow.addEventListener("resize", updateWysiwygStyle);
  }

  editable.onpointerdown = (event) => event.stopPropagation();

  // rAF (+ capture to by doubly sure) so we don't catch te pointerdown that
  // triggered the wysiwyg
  ownerWindow.requestAnimationFrame(() => {
    ownerWindow.addEventListener("pointerdown", onPointerDown, {
      capture: true,
    });
  });
  ownerWindow.addEventListener("beforeunload", handleSubmit);
  editorBox!.appendChild(editable);

  return handleSubmit;
};
